/**
 * useWidgetLiveData — consumer for the server-driven widget push pipeline.
 *
 * The backend ships a live-push stack (WidgetUpdateHub at /hubs/widgets,
 * SignalRWidgetUpdatePublisher, and a refresh worker that fetches each tile's
 * bound endpoint server-side every tick). This hook is the UI consumer that
 * stack was missing: it opens a `/hubs/widgets` connection via the shared
 * `useSignalR`, subscribes to the rendered tile's instance group
 * (`SubscribeWidget(instanceId)`), and surfaces the latest pushed data frame so
 * a widget renders server-driven updates instead of polling.
 *
 * Channel separation mirrors the backend: ordinary data frames arrive on
 * `WidgetDataUpdated` and are exposed as {@link UseWidgetLiveDataResult.data};
 * threshold-breach frames arrive on the dedicated `WidgetAlertRaised` method and
 * are exposed separately as {@link UseWidgetLiveDataResult.alert}, so a tile
 * routes a breach to its alert surface rather than mis-rendering it as data.
 *
 * Typical composition with {@link useWidgetData}: feed `live.data?.dataJson`
 * into `useWidgetData` as the live source, falling back to polling while the
 * hub is disconnected (`live.connectionState !== 'Connected'`).
 */

import * as React from 'react';
import { useSignalR, type ConnectionState } from '@tyndo/hooks';

/** Live data frame pushed over the `WidgetDataUpdated` hub method. */
export interface WidgetLiveDataFrame {
  widgetInstanceId: string;
  /** Widget-shaped JSON the tile renders directly. */
  dataJson: string;
  /** Monotonic revision so out-of-order frames can be dropped. */
  revision: number;
  pushedAt: string;
}

/** Alert frame pushed over the dedicated `WidgetAlertRaised` hub method. */
export interface WidgetLiveAlertFrame {
  widgetInstanceId: string;
  alertId: string;
  widgetId: string;
  seriesKey: string;
  name: string;
  severity: string;
  /** Edge kind: 'raised' | 'renotify' | 'cleared'. */
  kind: string;
  threshold: number;
  observedValue: number;
  revision: number;
  pushedAt: string;
}

export interface UseWidgetLiveDataResult<T = unknown> {
  /** Latest pushed data frame, or null until the first frame arrives. */
  data: WidgetLiveDataFrame | null;
  /**
   * The latest frame's `dataJson` parsed as `T`, or null when there is no frame
   * yet or the payload is not valid JSON. Convenience for the common case.
   */
  parsed: T | null;
  /** Latest pushed alert frame, or null when no breach has been pushed. */
  alert: WidgetLiveAlertFrame | null;
  /** Underlying hub connection state — drives a polling fallback. */
  connectionState: ConnectionState;
  /** True while the hub is connected and the instance subscription is live. */
  isLive: boolean;
}

const HUB = 'widgets'; // resolves to /hubs/widgets via useSignalR's hub map

/**
 * Subscribe a rendered widget tile to its live push group.
 *
 * @param widgetInstanceId Server-assigned instance id of the rendered tile.
 *   Pass an empty string / undefined to disable (e.g. a not-yet-persisted tile).
 */
export function useWidgetLiveData<T = unknown>(
  widgetInstanceId: string | undefined,
): UseWidgetLiveDataResult<T> {
  const { connection, connectionState } = useSignalR(HUB);
  const [data, setData] = React.useState<WidgetLiveDataFrame | null>(null);
  const [alert, setAlert] = React.useState<WidgetLiveAlertFrame | null>(null);
  // Track the latest revision so an out-of-order frame is dropped.
  const revisionRef = React.useRef<number>(-1);

  React.useEffect(() => {
    // Reset per-instance state when the subscription target changes.
    setData(null);
    setAlert(null);
    revisionRef.current = -1;

    if (!connection || connectionState !== 'Connected' || !widgetInstanceId) {
      return;
    }

    const onData = (...args: unknown[]) => {
      const frame = args[0] as WidgetLiveDataFrame | undefined;
      if (!frame || frame.widgetInstanceId !== widgetInstanceId) return;
      // Out-of-order / duplicate protection: when the frame carries a real
      // numeric revision and we have already seen one (ref > -1), drop anything
      // that does not strictly advance the revision (`<=` — equal revisions are
      // duplicate re-pushes that would only cause redundant re-renders).
      if (
        typeof frame.revision === 'number' &&
        revisionRef.current >= 0 &&
        frame.revision <= revisionRef.current
      ) {
        return;
      }
      // A missing/non-numeric revision is monotonic-unknown: render the frame
      // once but leave the ref untouched so a later numbered frame can still be
      // ordered against the last real revision we saw.
      if (typeof frame.revision === 'number') {
        revisionRef.current = frame.revision;
      }
      setData(frame);
    };

    const onAlert = (...args: unknown[]) => {
      const frame = args[0] as WidgetLiveAlertFrame | undefined;
      if (!frame || frame.widgetInstanceId !== widgetInstanceId) return;
      setAlert(frame);
    };

    connection.on('WidgetDataUpdated', onData);
    connection.on('WidgetAlertRaised', onAlert);

    // Join the per-instance group server-side. Idempotent on the hub.
    connection
      .invoke('SubscribeWidget', widgetInstanceId)
      .catch((err) => console.error('useWidgetLiveData: SubscribeWidget failed', err));

    return () => {
      connection.off('WidgetDataUpdated', onData);
      connection.off('WidgetAlertRaised', onAlert);
      connection
        .invoke('UnsubscribeWidget', widgetInstanceId)
        .catch(() => {
          /* connection may already be closing — nothing to clean up */
        });
    };
  }, [connection, connectionState, widgetInstanceId]);

  const parsed = React.useMemo<T | null>(() => {
    if (!data?.dataJson) return null;
    try {
      return JSON.parse(data.dataJson) as T;
    } catch {
      return null;
    }
  }, [data]);

  const isLive = connectionState === 'Connected' && Boolean(widgetInstanceId);

  return { data, parsed, alert, connectionState, isLive };
}
